import { InputGroup, Form, DropdownButton, Dropdown, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FaSearch } from "react-icons/fa";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import YearSelect from "../../../Shipping/VersionSelector/components/SearchExport/YearSelect";
import "../../../Shipping/VersionSelector/components/SearchExport/SearchExport.css";

interface SearchExportProps {
  searchTerm?: string;
  setSearchTerm?: (v: string) => void;
  exportData?: Record<string, unknown>[];
  fileName?: string;
  setFilteredData?: (v: unknown) => void;
  selectedYear?: number;
  setSelectedYear?: (v: number) => void;
  availableYears?: number[];
  /** Quando informado, exibe o ano como texto fixo (sem dropdown) com este tooltip. */
  yearTooltip?: string;
}

const normalizarTexto = (texto: string) =>
  texto?.replace(/[^\d\w]/gi, "").toLowerCase();

const SearchExport = ({
  searchTerm = "",
  setSearchTerm,
  exportData = [],
  fileName = "ListaSindicatosDocumentacao",
  setFilteredData,
  selectedYear,
  setSelectedYear,
  availableYears = [2026, 2025],
  yearTooltip,
}: SearchExportProps) => {
  const isYearMode =
    typeof selectedYear === "number" && typeof setSelectedYear === "function";

  const yearSelectEl = isYearMode ? (
    yearTooltip ? (
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip id="year-select-static-tooltip">{yearTooltip}</Tooltip>}
      >
        <span
          className="year-select"
          role="status"
          aria-label={`Ano de referência: ${selectedYear}. ${yearTooltip}`}
        >
          <span className="year-select__trigger year-select__trigger--static">
            <span className="year-select__value">{selectedYear}</span>
          </span>
        </span>
      </OverlayTrigger>
    ) : (
      <YearSelect
        value={selectedYear}
        onChange={setSelectedYear}
        options={availableYears}
        aria-label="Selecionar ano de referência da análise"
      />
    )
  ) : null;

  const exportToExcel = () => {
    if (!exportData.length) return;
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `${fileName}.xlsx`);
  };

  const exportToPDF = () => {
    if (!exportData.length) return;

    const doc = new jsPDF({ orientation: "landscape" });
    const columns = Object.keys(exportData[0]) as string[];
    const rows = exportData.map((item) =>
      columns.map((col) => item[col] as string | number),
    );

    doc.setFontSize(14);
    doc.text(fileName.toUpperCase(), 14, 15);

    autoTable(doc, {
      startY: 20,
      head: [columns],
      body: rows as (string | number)[][],
      styles: {
        fontSize: 10,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [52, 58, 64],
        textColor: [255, 255, 255],
        halign: "center",
      },
      bodyStyles: {
        halign: "left",
      },
      alternateRowStyles: {
        fillColor: [240, 240, 240],
      },
    });

    doc.save(`${fileName}.pdf`);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm?.(value);

    if (setFilteredData && typeof setFilteredData === "function") {
      const termoNormalizado = normalizarTexto(value);

      const novaLista = exportData.filter((item) => {
        const cnpjNormalizado = normalizarTexto(String(item.cnpj ?? ""));
        const sigla = String(item.sigla ?? "").toLowerCase();
        return (
          cnpjNormalizado.includes(termoNormalizado) ||
          sigla.includes(termoNormalizado)
        );
      });

      setFilteredData(novaLista);
    }
  };

  const searchField = (
    <InputGroup className="analysis-search-export-input flex-grow-1 min-w-0">
      <InputGroup.Text className="search-icon">
        <FaSearch />
      </InputGroup.Text>
      <Form.Control
        type="text"
        placeholder="Sigla ou CNPJ"
        value={searchTerm}
        onChange={handleSearchChange}
      />
    </InputGroup>
  );

  const exportMenu = (
    <DropdownButton
      title="Exportar"
      className="custom-export-btn flex-shrink-0"
      variant="primary"
      align="end"
    >
      <Dropdown.Item onClick={exportToPDF}>Documento (.pdf)</Dropdown.Item>
      <Dropdown.Item onClick={exportToExcel}>Excel (.xlsx)</Dropdown.Item>
    </DropdownButton>
  );

  const searchAndExport = (
    <div className="analysis-search-export-controls d-flex gap-2 align-items-center flex-grow-1 min-w-0">
      {searchField}
      {exportMenu}
    </div>
  );

  if (isYearMode) {
    return (
      <div className="shipping-top-actions analysis-search-export-year-bar search-export-section d-flex gap-2 align-items-center flex-nowrap">
        {yearSelectEl}
        {searchAndExport}
      </div>
    );
  }

  return (
    <div className="search-export-section d-flex gap-2 align-items-center">
      {searchField}
      {exportMenu}
    </div>
  );
};

export default SearchExport;
